import {
  editorHtmlToPlaceholders,
  parseFormLinkButtonPlaceholderSegments,
  repairMalformedFormLinkPlaceholders,
} from "@/lib/forms/email-editor-placeholders";
import {
  deriveSharedSettings,
  deriveTemplateOverrides,
  resolveEffectiveEmailTemplate,
  type EmailTemplatesRecord,
} from "@/lib/forms/email-shared-settings";
import { applySignoffToBody } from "@/lib/forms/email-signoff";
import type { SavedCustomEmailTemplatesMap } from "@/lib/forms/email-saved-custom-templates";
import type { FilledFilesFormat } from "@/features/forms/_logic/constants/file-constants";
import { unwrapAnswer } from "@/lib/forms/answer-matching";

/** Sender addresses available for form emails. Mirrors the options used by the landing-page-v2 activity editor. */
export const SEND_FROM_OPTIONS: { value: string; label: string }[] = [
  { value: "events@nowadays.ai", label: "events@nowadays.ai" },
];

/** Coerces a stored value to an allowed option, falling back to the first option for legacy/unknown values. */
export const resolveAllowedSendFrom = (value: string | undefined): string =>
  SEND_FROM_OPTIONS.some((o) => o.value === value) ? (value as string) : SEND_FROM_OPTIONS[0].value;

export const DEFAULT_EMAIL_TEMPLATES = {
  reminder: {
    subject: "Urgent: Action Required - {{form_name}}",
    body: `<p>Hi {{first_name}},</p><p><strong>This is an urgent reminder</strong> to complete your form for {{form_name}}. <strong>The deadline is quickly approaching</strong>, and we need your response as soon as possible.</p>{{form_link_invite_button}}<p>Please take a moment to fill out the form now to ensure we have your information on time.</p><p>If you have any questions or need assistance, please don't hesitate to reach out.</p><p>{{signoff}}</p>`,
    sendFrom: "events@nowadays.ai",
    senderName: "Nowadays Events",
    replyTo: "events@nowadays.ai",
  },
  confirmation: {
    subject: "Form Submission Confirmed - {{form_name}}",
    body: `<p>Hi {{first_name}},</p><p>Thank you for your submission!</p><p>We've successfully received your submission for {{form_name}}.</p>{{form_link_view_edit_button}}<p>If you have any questions, feel free to reach out to our team at events@nowadays.ai.</p><p>{{signoff}}</p>`,
    sendFrom: "events@nowadays.ai",
    senderName: "Nowadays Events",
    replyTo: "events@nowadays.ai",
  },
  confirmation_not_attending: {
    subject: "Sorry we'll miss you - {{form_name}}",
    body: `<p>Hi {{first_name}},</p><p>Thank you for letting us know you're unable to attend {{form_name}}. Should your plans change, you can update your response any time using the button below.</p>{{form_link_view_edit_button}}<p>{{signoff}}</p>`,
    sendFrom: "events@nowadays.ai",
    senderName: "Nowadays Events",
    replyTo: "events@nowadays.ai",
  },
  approval: {
    subject: "Your Submission Has Been Approved - {{form_name}}",
    body: `<p>Hi {{first_name}},</p><p>Great news! Your submission for {{form_name}} has been approved. You'll receive additional information shortly.</p><p>{{qr_code}}If you have any questions, feel free to reach out.</p><p>{{signoff}}</p>`,
    sendFrom: "events@nowadays.ai",
    senderName: "Nowadays Events",
    replyTo: "events@nowadays.ai",
  },
  rejection: {
    subject: "Update on Your Submission - {{form_name}}",
    body: `<p>Hi {{first_name}},</p><p>Thank you for your submission to {{form_name}}.</p><p>Unfortunately, we have rejected your submission. If you have any questions, feel free to reach out.</p><p>{{signoff}}</p>`,
    sendFrom: "events@nowadays.ai",
    senderName: "Nowadays Events",
    replyTo: "events@nowadays.ai",
  },
  invitation: {
    subject: "You're Invited to Complete {{form_name}}",
    body: `<p>Hi {{first_name}},</p><p>You've been invited to complete {{form_name}}.</p>{{form_link_invite_button}}<p>If you have any questions, feel free to reach out.</p><p>{{signoff}}</p>`,
    sendFrom: "events@nowadays.ai",
    senderName: "Nowadays Events",
    replyTo: "events@nowadays.ai",
  },
  qr_code: {
    subject: "Your Check-in QR Code - {{form_name}}",
    body: `<p>Hi {{first_name}},</p><p>Here is your QR code for check-in at {{form_name}}.</p><p>{{qr_code}}</p><p>Please show this QR code at the event for check-in.</p><p>If you have any questions, feel free to reach out.</p><p>{{signoff}}</p>`,
    sendFrom: "events@nowadays.ai",
    senderName: "Nowadays Events",
    replyTo: "events@nowadays.ai",
  },
  file_delivery: {
    subject: "Your completed {{form_name}}",
    body: `<p>Hi {{first_name}},</p><p>Thanks for completing {{form_name}}. Your completed document is attached to this email.</p><p>If you have any questions, feel free to reach out.</p><p>{{signoff}}</p>`,
    sendFrom: "events@nowadays.ai",
    senderName: "Nowadays Events",
    replyTo: "events@nowadays.ai",
  },
};

export type EmailTemplateType =
  | "reminder"
  | "confirmation"
  | "confirmation_not_attending"
  | "approval"
  | "rejection"
  | "invitation"
  | "qr_code"
  | "file_delivery";

export type EmailActionType = EmailTemplateType | "custom";

/** Display labels for each system template type, e.g. for a template-picker dropdown. */
export const EMAIL_TEMPLATE_TYPE_LABELS: Record<EmailTemplateType, string> = {
  reminder: "Reminder Email",
  invitation: "Invitation Email",
  confirmation: "Confirmation Email",
  confirmation_not_attending: "Confirmation Email (Not Attending)",
  approval: "Approval Email",
  rejection: "Rejection Email",
  qr_code: "QR Code Email",
  file_delivery: "File delivery",
};

export interface EmailBranding {
  /** Header image URL shown at the top of every email. */
  bannerUrl?: string;
  /** When true, header image is displayed compact/logo-style (max 400×120 px). Default: full-width banner. */
  headerCompact?: boolean;
  /** Horizontal alignment of the header logo/banner. Default: "center". */
  logoAlignment?: "left" | "center";
  /** Optional px cap for the header logo width, overriding the 400 (compact) / 600 (banner) default — use to shrink an oversized logo. */
  logoMaxWidthPx?: number;
  /** Hex color used for action buttons (default: #5261EB). */
  primaryColor?: string;
  /** Background color of the email content area (default: #ffffff). */
  backgroundColor?: string;
  /** Custom footer text (default: © Year Nowadays AI, Corp.). */
  footerText?: string;
  /** Sign-off lines (e.g. "Best regards,\\nJane") — shared across all template types. */
  signoffText?: string;
  /** Optional signature image shown above sign-off text. */
  signoffImageUrl?: string;
}

/** Preset header-logo widths behind the "Small / Large" control in the email design panel. */
export const EMAIL_LOGO_SIZE_PX = { small: 160, large: 400 } as const;

/**
 * Height caps paired with {@link EMAIL_LOGO_SIZE_PX}. A compact/form logo is bounded by BOTH width
 * and height, and for a landscape logo (icon + wordmark) the height is usually the binding
 * dimension — so a width-only Small/Large control changes nothing visible. Moving the height too
 * guarantees "small" always renders smaller. "large" keeps the historical 120px cap; "small" halves it.
 */
export const EMAIL_LOGO_MAX_HEIGHT_PX = { small: 60, large: 120 } as const;

/**
 * Resolves the header logo's width cap, height cap, and horizontal alignment from branding tokens.
 * Shared by the send path and the editor preview so the two can't drift; defaults preserve the
 * historical centered 400px-wide × 120px-tall (compact logo) / 600px-wide (banner) rendering.
 *
 * `maxHeightPx` applies only to compact/form logos (full-width banners are width-driven). It's keyed
 * off the width preset so the Small/Large control produces a visible change regardless of the logo's
 * aspect ratio: the small preset (≤160px wide) also drops to a 60px height cap; everything else keeps 120px.
 *
 * @param branding - Resolved email branding tokens (logo width/alignment).
 * @param isFormLogo - True for a compact/form logo (height-capped), false for a full-width banner.
 * @returns The logo's max width (px), max height (px — compact only), and horizontal alignment.
 */
export function resolveLogoLayout(
  branding: EmailBranding | undefined,
  isFormLogo: boolean
): { maxWidthPx: number; maxHeightPx: number; align: "left" | "center" } {
  // Coerce from JSONB defensively — the value lands raw in an email `style` attribute, so a
  // non-numeric stored value must never reach the HTML. Fall back to the historical default.
  const parsed = Number(branding?.logoMaxWidthPx);
  const maxWidthPx = Number.isFinite(parsed) && parsed > 0 ? parsed : isFormLogo ? 400 : 600;
  return {
    maxWidthPx,
    maxHeightPx:
      maxWidthPx <= EMAIL_LOGO_SIZE_PX.small ? EMAIL_LOGO_MAX_HEIGHT_PX.small : EMAIL_LOGO_MAX_HEIGHT_PX.large,
    align: branding?.logoAlignment === "left" ? "left" : "center",
  };
}

export interface EmailTemplate {
  subject: string;
  body: string;
  sendFrom?: string;
  /** Display name shown in the recipient's inbox (e.g. "Nowadays Events"). */
  senderName?: string;
  /** One or more reply-to addresses, comma-separated (same convention as `cc`). Split into a list
   *  at send time; 2+ addresses send via SendGrid's `reply_to_list`. */
  replyTo?: string;
  cc?: string;
  includeCalendarInvite?: boolean;
  /** When set, emails are sent to the value of this form field instead of the default "Email" field. */
  sendToField?: string;
  /** Per-template branding: banner, logo, colors, footer. */
  branding?: EmailBranding;
  /**
   * `file_delivery` only: which filled file format(s) to attach to the delivery email.
   * Ignored by every other template type. Defaults to "both" when unset.
   */
  attachFormat?: FilledFilesFormat;
}

/** EmailTemplate with required fields guaranteed (e.g., after applying defaults). */
export type EmailTemplateWithDefaults = Required<Pick<EmailTemplate, "subject" | "body" | "sendFrom" | "replyTo">> &
  Pick<EmailTemplate, "cc" | "includeCalendarInvite" | "sendToField" | "senderName" | "branding">;

export interface FormEmailTemplates {
  reminder?: EmailTemplate;
  confirmation?: EmailTemplate;
  /** Sent instead of `confirmation` when the submission's RSVP answer marks it as declining —
   *  see `confirmation_branch` on {@link EmailTemplatesRecord} and {@link resolveConfirmationTemplateForSubmission}. */
  confirmation_not_attending?: EmailTemplate;
  approval?: EmailTemplate;
  rejection?: EmailTemplate;
  invitation?: EmailTemplate;
  qr_code?: EmailTemplate;
  file_delivery?: EmailTemplate;
  /** User-authored custom templates from the Emails tab, keyed by id — see `email-saved-custom-templates`. */
  saved_custom_templates?: SavedCustomEmailTemplatesMap;
}

/**
 * Gets the appropriate email template for a form.
 * Uses custom template if available, otherwise falls back to default.
 */
export function getFormEmailTemplate(
  templateType: EmailTemplateType,
  customTemplates?: FormEmailTemplates | null
): EmailTemplate {
  const raw = customTemplates as EmailTemplatesRecord | null | undefined;
  const defaultTemplate = DEFAULT_EMAIL_TEMPLATES[templateType];
  const customTemplate = customTemplates?.[templateType];

  const content: EmailTemplate =
    customTemplate?.subject && customTemplate?.body
      ? {
          subject: customTemplate.subject,
          body: customTemplate.body,
          sendFrom: customTemplate.sendFrom || defaultTemplate.sendFrom,
          senderName: customTemplate.senderName || defaultTemplate.senderName,
          replyTo: customTemplate.replyTo || defaultTemplate.replyTo,
          cc: customTemplate.cc,
          includeCalendarInvite: customTemplate.includeCalendarInvite,
          sendToField: customTemplate.sendToField,
          branding: customTemplate.branding,
          attachFormat: customTemplate.attachFormat,
        }
      : defaultTemplate;

  if (raw?.shared_settings) {
    return resolveEffectiveEmailTemplate(
      templateType,
      content,
      deriveSharedSettings(raw),
      deriveTemplateOverrides(raw)
    );
  }

  return content;
}

/**
 * Resolves a form's confirmation email template from its raw, loosely-typed
 * `email_templates` column (the DB projection type doesn't structurally match
 * `FormEmailTemplates`, hence the cast). Centralizes the cast so every caller
 * that only needs the confirmation template doesn't repeat it.
 */
export function getConfirmationTemplate(rawEmailTemplates: unknown): EmailTemplate {
  return getFormEmailTemplate("confirmation", (rawEmailTemplates ?? null) as FormEmailTemplates | null);
}

/**
 * Optional per-form config that routes a submission to the "not attending" confirmation variant.
 * Stored on the form's `email_templates` JSONB (see `EmailTemplatesRecord.confirmation_branch`).
 * `fieldLabel` is the RSVP question's label; `notAttendingValues` are the answer values that mean
 * "declining" (e.g. ["No"]). A form without this config never branches.
 */
export interface ConfirmationBranchConfig {
  fieldLabel: string;
  notAttendingValues: string[];
}

/**
 * Reads the confirmation branch config from a form's raw `email_templates`, if present and
 * structurally valid (a config with an empty `fieldLabel` or no answers is still returned — it's
 * the editor's in-progress state; use {@link isConfirmationBranchComplete} to gate on activeness).
 *
 * @param rawEmailTemplates - The form's raw `email_templates` JSONB (or any object holding `confirmation_branch`).
 * @returns The parsed config, or null when absent/malformed.
 */
export function getConfirmationBranchConfig(rawEmailTemplates: unknown): ConfirmationBranchConfig | null {
  const cfg = (rawEmailTemplates as { confirmation_branch?: unknown } | null | undefined)?.confirmation_branch as
    | Partial<ConfirmationBranchConfig>
    | undefined;
  if (!cfg || typeof cfg.fieldLabel !== "string" || !Array.isArray(cfg.notAttendingValues)) return null;
  return { fieldLabel: cfg.fieldLabel, notAttendingValues: cfg.notAttendingValues.map(String) };
}

/**
 * Whether a branch config is complete enough to actually route submissions — i.e. it names a
 * question and at least one declining answer. An incomplete config (toggle flipped on but not yet
 * filled in) would silently match nothing, so callers persist branching only when this is true.
 *
 * @param cfg - The branch config to check (null/undefined counts as incomplete).
 * @returns True when the config both names a field and lists ≥1 not-attending value.
 */
export function isConfirmationBranchComplete(cfg: ConfirmationBranchConfig | null | undefined): boolean {
  return Boolean(cfg && cfg.fieldLabel.trim() !== "" && cfg.notAttendingValues.length > 0);
}

/**
 * Whether a submission's answer to the branch field marks it as "not attending". Case/whitespace
 * insensitive; handles both scalar and array (multi-select) answers. Returns false when the form
 * has no branch config — so non-RSVP forms always take the normal confirmation path.
 *
 * @param rawEmailTemplates - The form's raw `email_templates` JSONB.
 * @param formData - The submitted answers keyed by field label.
 * @returns True when the submission should receive the not-attending confirmation variant.
 */
export function submissionIsNotAttending(rawEmailTemplates: unknown, formData: Record<string, unknown>): boolean {
  const cfg = getConfirmationBranchConfig(rawEmailTemplates);
  if (!cfg) return false;
  const answer = formData?.[cfg.fieldLabel];
  const answered = (Array.isArray(answer) ? answer : [answer]).map((v) =>
    String(unwrapAnswer(v) ?? "")
      .toLowerCase()
      .trim()
  );
  const declining = new Set(cfg.notAttendingValues.map((v) => v.toLowerCase().trim()));
  return answered.some((v) => declining.has(v));
}

/**
 * Picks the confirmation email VARIANT (kind string) for a submission: "confirmation_not_attending"
 * when the submission declines AND the form actually authored that variant, otherwise the normal
 * "confirmation". A blank not-attending variant deliberately falls back to the attending copy rather
 * than sending the generic default, so a decline never yields an unwritten email.
 *
 * Callers that need this kind string (not just the resolved content) must use it — e.g. to key the
 * unified-send cutover lookup (`resolveFormsSendMode`) — rather than hardcoding "confirmation",
 * which would route a declining submission through the unified pipeline's ATTENDING template.
 *
 * @param rawEmailTemplates - The form's raw `email_templates` JSONB.
 * @param formData - The submitted answers keyed by field label.
 * @returns "confirmation_not_attending" or "confirmation".
 */
export function resolveConfirmationKindForSubmission(
  rawEmailTemplates: unknown,
  formData: Record<string, unknown>
): "confirmation" | "confirmation_not_attending" {
  const raw = (rawEmailTemplates ?? null) as FormEmailTemplates | null;
  if (submissionIsNotAttending(rawEmailTemplates, formData)) {
    const authored = raw?.confirmation_not_attending;
    if (authored?.subject && authored?.body) return "confirmation_not_attending";
  }
  return "confirmation";
}

/**
 * Picks the confirmation email variant's resolved (defaults-applied) content for a submission — see
 * {@link resolveConfirmationKindForSubmission} for the variant-selection rule.
 *
 * @param rawEmailTemplates - The form's raw `email_templates` JSONB.
 * @param formData - The submitted answers keyed by field label.
 * @returns The resolved (defaults-applied) email template to send.
 */
export function resolveConfirmationTemplateForSubmission(
  rawEmailTemplates: unknown,
  formData: Record<string, unknown>
): EmailTemplate {
  const raw = (rawEmailTemplates ?? null) as FormEmailTemplates | null;
  return getFormEmailTemplate(resolveConfirmationKindForSubmission(rawEmailTemplates, formData), raw);
}

export interface ReplaceEmailVariablesOptions {
  firstName?: string;
  fullName?: string;
  email?: string;
  formName?: string;
  formLink?: string;
  wrapStandaloneLinks?: boolean;
  qrCodeHtml?: string;
  /** Pre-built inline HTML for the {{barcode}} element (barcode <img>), per recipient. */
  barcodeHtml?: string;
  eventLocation?: string;
  /** Check-in date string (e.g. "July 19, 2026") for {{checkin_date}} substitution. */
  checkinDate?: string;
  /** Check-out date string (e.g. "July 23, 2026") for {{checkout_date}} substitution. */
  checkoutDate?: string;
  /** Seat/section assignment (e.g. "Sec 102, Row C, Seat 4") for {{seat_assignment}} substitution. Sanitize before passing. */
  seatAssignment?: string;
  /** Hotel booking confirmation number for {{hotel_confirmation}} substitution. Sanitize before passing. */
  hotelConfirmation?: string;
  /** Hotel/property name (e.g. "Conrad Los Angeles") for {{hotel_name}} substitution. Sanitize before passing. */
  hotelName?: string;
  /** Pre-sanitized form answer values keyed by field label, for {{answer:Label}} substitution. */
  formAnswers?: Record<string, string>;
  /** CTA / link accent color (enterprise primary when set). */
  primaryColor?: string;
  /** Sign-off branding applied after variable substitution. */
  branding?: EmailBranding;
}

/**
 * Replaces email template variables in subject or body text.
 * Handles {{first_name}}, {{full_name}}, {{email}}, {{form_name}}, {{form_link}}, {{form_link_invite_button}}, {{form_link_view_edit_button}}, {{event_location}}, {{checkin_date}}, {{checkout_date}}, {{seat_assignment}}, {{hotel_confirmation}}, and {{hotel_name}} placeholders.
 *
 * @param text - The template text to replace variables in
 * @param options - Object containing replacement values
 * @returns Text with variables replaced
 */
export function replaceEmailVariables(text: string, options: ReplaceEmailVariablesOptions): string {
  const {
    firstName = "",
    fullName = "",
    email = "",
    formName = "",
    formLink = "",
    wrapStandaloneLinks = false,
    qrCodeHtml = "",
    barcodeHtml = "",
    eventLocation = "",
    checkinDate = "",
    checkoutDate = "",
    seatAssignment = "",
    hotelConfirmation = "",
    hotelName = "",
    formAnswers = {},
    primaryColor = "#5261EB",
    branding,
  } = options;

  let result = repairMalformedFormLinkPlaceholders(text)
    .replace(/\{\{first_name\}\}/g, firstName)
    .replace(/\{\{full_name\}\}/g, fullName)
    .replace(/\{\{email\}\}/g, email)
    .replace(/\{\{form_name\}\}/g, formName)
    .replace(/\{\{qr_code\}\}/g, qrCodeHtml)
    .replace(/\{\{barcode\}\}/g, barcodeHtml)
    .replace(/\{\{event_location\}\}/g, eventLocation)
    .replace(/\{\{checkin_date\}\}/g, checkinDate)
    .replace(/\{\{checkout_date\}\}/g, checkoutDate)
    .replace(/\{\{seat_assignment\}\}/g, seatAssignment)
    .replace(/\{\{hotel_confirmation\}\}/g, hotelConfirmation)
    .replace(/\{\{hotel_name\}\}/g, hotelName);

  // Handle {{custom_button:URL:TEXT}} replacements
  result = result.replace(/\{\{custom_button:(.+):(.+)\}\}/g, (match, url, text) => {
    const cleanUrl = url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
    const escapedText = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    return `<div style="text-align: center; margin: 20px 0;"><a href="${cleanUrl}" style="display: inline-block; padding: 12px 24px; background-color: ${primaryColor}; color: #ffffff !important; text-decoration: none; border-radius: 6px; font-weight: 600;">${escapedText}</a></div>`;
  });

  // Handle {{form_link_invite_button}} / {{form_link_view_edit_button}} (optional :label and :color)
  const buildFormLinkButtonHtml = (label: string, url: string, buttonColor: string) => {
    const escapedText = label
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
    return `<div style="text-align: center; margin: 20px 0;"><a href="${url}" style="display: inline-block; padding: 12px 24px; background-color: ${buttonColor}; color: #ffffff !important; text-decoration: none; border-radius: 6px; font-weight: 600;">${escapedText}</a></div>`;
  };

  const replaceFormLinkButtonPlaceholder = (
    templateKey: "form_link_invite_button" | "form_link_view_edit_button",
    defaultLabel: string,
    url: string
  ) => {
    result = result.replace(
      new RegExp(`\\{\\{${templateKey}(?::([^:}]*))?(?::([^}]*))?\\}\\}`, "g"),
      (_match, seg1?: string, seg2?: string) => {
        const { label, color } = parseFormLinkButtonPlaceholderSegments(seg1, seg2);
        return buildFormLinkButtonHtml(label?.trim() || defaultLabel, url, color || primaryColor);
      }
    );
  };

  if (formLink) {
    const cleanFormLink =
      formLink.startsWith("http://") || formLink.startsWith("https://") ? formLink : `https://${formLink}`;

    replaceFormLinkButtonPlaceholder("form_link_invite_button", "Fill Out Form", cleanFormLink);
    replaceFormLinkButtonPlaceholder("form_link_view_edit_button", "View or Edit Submission", cleanFormLink);
  } else {
    result = result.replace(/\{\{form_link_invite_button(?::([^:}]*))?(?::([^}]*))?\}\}/g, "");
    result = result.replace(/\{\{form_link_view_edit_button(?::([^:}]*))?(?::([^}]*))?\}\}/g, "");
  }

  // Handle {{answer:Label}} replacements - substitute form response values by field label
  result = result.replace(/\{\{answer:(.+?)\}\}/g, (_match, label: string) => {
    return formAnswers[label] ?? "";
  });

  // Handle {{form_link}} replacements - must be done in correct order
  if (formLink) {
    // Ensure formLink doesn't already have protocol to avoid double protocols
    const cleanFormLink =
      formLink.startsWith("http://") || formLink.startsWith("https://") ? formLink : `https://${formLink}`;

    // First, replace complete link tags that have {{form_link}} in both href and text
    result = result.replace(
      /<a\s+[^>]*href\s*=\s*["']\{\{form_link\}\}["'][^>]*>\s*\{\{form_link\}\}\s*<\/a>/gi,
      wrapStandaloneLinks
        ? `<a href="${cleanFormLink}" style="color: ${primaryColor};">${cleanFormLink}</a>`
        : `<a href="${cleanFormLink}">${cleanFormLink}</a>`
    );
    // Then replace in href attributes only (handle cases where href might have https:// prefix)
    result = result.replace(/href\s*=\s*["']https?:\/\/\{\{form_link\}\}["']/gi, `href="${cleanFormLink}"`);
    result = result.replace(/href\s*=\s*["']\{\{form_link\}\}["']/gi, `href="${cleanFormLink}"`);
    // Then replace in link text content between > and <
    result = result.replace(/>\s*\{\{form_link\}\}\s*</g, `>${cleanFormLink}<`);
    // Finally replace any remaining standalone {{form_link}} text
    if (wrapStandaloneLinks) {
      result = result.replace(
        /\{\{form_link\}\}/g,
        `<a href="${cleanFormLink}" style="color: ${primaryColor};">${cleanFormLink}</a>`
      );
    } else {
      result = result.replace(/\{\{form_link\}\}/g, cleanFormLink);
    }
  }

  return result;
}

/**
 * Resolves template body for preview (normalizes editor button HTML, then substitutes variables).
 */
export const resolveEmailBodyForPreview = (body: string, options: ReplaceEmailVariablesOptions): string => {
  const hasButtonPlaceholders =
    body.includes("{{form_link_invite_button") || body.includes("{{form_link_view_edit_button");
  const defaultButtonColor = options.primaryColor ?? options.branding?.primaryColor ?? "#5261EB";
  const normalized = hasButtonPlaceholders ? body : editorHtmlToPlaceholders(body, defaultButtonColor);
  const withVariables = replaceEmailVariables(normalized, options);
  return applySignoffToBody(withVariables, options.branding);
};
